import React, { useState } from "react";
import { useQuery } from "react-query";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FanReportTableMatch from "./FanReportTableMatch";
import { ButtonWithIcon, Container, FileDownloader } from "../../lib";
import { downloadAttendeesReportMatch, getAllAttendeesPerMatch } from "../../../../api/report/reportApi";
import BackBtn from "../../lib/BackBtn";

interface Props {
  matchId: string;
}

const FanReportListMatch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromQuery = searchParams.get("page");
  const matchIdFromQuery = searchParams.get("matchId");
  const page = pageFromQuery ? parseInt(pageFromQuery) : 1;
  const [nationalCode, setNationalCode] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [queryString, setQueryString] = React.useState<{
    page: number | string;
    search: string;
    filter: string | number;
    numberInPage: number | string;
    timeFrame: string;
  }>({
    page,
    search: "",
    filter: "",
    numberInPage: 5,
    timeFrame: "all",
  });
  const location = useLocation();
  const { state } = location;
  const matchId = matchIdFromQuery ? parseInt(matchIdFromQuery) : (state as number);

  const { data, isLoading, isError, refetch } = useQuery(
    [
      "finisedMatchesList",
      queryString.page,
      queryString.numberInPage,
      queryString.search
    ],
    () =>
      getAllAttendeesPerMatch(
        Number(matchId),
        Number(queryString.page),
        Number(queryString.numberInPage),
        queryString.search
      ),
    {
      enabled: isSubmitted && !!nationalCode
    }
  );

  const { refetch: exportToExcel } = useQuery(
    ["export-to-excel"],
    () => downloadAttendeesReportMatch(Number(matchId)),
    {
      enabled: false,
    }
  );

  const handleNationalCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nationalCode) {
      setIsSubmitted(true);
      setQueryString(prev => ({
        ...prev,
        search: nationalCode,
        page: 1 // Reset to first page on new search
      }));
      refetch();
    }
  };

  const handleReset = () => {
    setNationalCode("");
    setIsSubmitted(false);
    setQueryString(prev => ({
      ...prev,
      search: "",
      page: 1
    }));
    setSearchParams({ matchId: matchId.toString() });
  };

  const setQS = (page) => {
    setSearchParams({ page: page.toString(), matchId: matchId.toString() });
    setQueryString((state) => ({
      ...state,
      page: page,
    }));
  };

  const handleBack = () => {
    window.history.back();
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error: </div>;

  return (
    <Container className={"bg-white p-0 mt-10"}>
      <div className="d-flex align-items-center justify-content-between table-header">
        <div className="d-flex align-items-center gap-4">
          <h3> لیست هوادار </h3>
          <FileDownloader
            downloadFunc={() =>
              downloadAttendeesReportMatch(Number(matchId))
            }
          />
        </div>
        <BackBtn onClick={handleBack} />
      </div>
      
      <div className="p-4">
        <form onSubmit={handleNationalCodeSubmit} className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-3">
            <input
              type="text"
              className="border rounded p-2 bg-white w-c-130 text-right"
              placeholder="کد ملی را وارد کنید..."
              value={nationalCode}
              onChange={(e) => setNationalCode(e.target.value)}
              pattern="[0-9]{10}"
              maxLength={10}
              required
            />
            <ButtonWithIcon
              btnText="جستجو"
              iconName="magnifier"
              type="submit"
            />
            <ButtonWithIcon
              btnText="پاک کردن"
              iconName="trash"
              type="button"
              onClick={handleReset}
            />
          </div>
        </form>

        {isSubmitted && (
          <>
            {data?.attendees && data.attendees.length > 0 ? (
              <FanReportTableMatch
                queryString={queryString}
                setQueryString={setQS}
                data={data}
                refetch={refetch}
                numberInPage={Number(queryString.numberInPage)}
                matchId={Number(matchId)}
              />
            ) : (
              <p className={"p-2 m-2"}>{`اطلاعاتی برای کد ملی ${nationalCode} یافت نشد`}</p>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

const SearchForm = ({ onSubmitCallback, searchTerm }) => {
  const navigate = useNavigate();
  const initialValues = {
    searchTerm: searchTerm || "",
  };

  const onSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
    if (onSubmitCallback && typeof onSubmitCallback === "function") {
      onSubmitCallback(values.searchTerm);
    }
  };

  return (
    <Formik
    initialValues={{ searchTerm: searchTerm || "" }}
    onSubmit={(values, { setSubmitting }) => {
      setSubmitting(false);
      if (onSubmitCallback) {
        onSubmitCallback(values.searchTerm);
      }
    }}
  >
    {({ isSubmitting, handleChange, values }) => (
      <Form 
        className={"d-flex align-items-center gap-3"}
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        <Field
          type="text"
          name="searchTerm"
          className="border rounded p-2 bg-white w-c-130 text-right"
          placeholder="جستجو"
          onChange={handleChange}
          value={values.searchTerm}
        />
        <ErrorMessage name="searchTerm" component="div" className="text-red-600" />
        <ButtonWithIcon
          btnText="جستجو"
          iconName="magnifier"
          type="submit"
          disabled={isSubmitting}
        />
      </Form>
    )}
  </Formik>
  );
};

export default FanReportListMatch;
